import React, { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Col, Container, Row, Form, InputGroup, Button } from "react-bootstrap";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import IconHeader from "../../images/logo-lg.png";
import QuoteSlider from "./QuoteSlider";
import IocnSuccess from "../../images/success-icon.png";
import apiConfig from "./apiConfig";
const MySwal = withReactContent(Swal);


const quotes = [
    "Easily discover and quickly benefit from the most advanced technologies.",
    "Expert-level insights at your fingertips.",
    "Expedite planning, fieldwork and completion procedures.",
    "Elevate your expertise and confidence.",
    "AI-powered audits: Save time, increase profitability, and improve quality.",
];

const validationSchema = Yup.object({
    password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters long")
        .matches(/[A-Z]/, "Must include at least one uppercase letter")
        .matches(/[a-z]/, "Must include at least one lowercase letter")
        .matches(/\d/, "Must include at least one number")
        .matches(/[!@#$%^&*(),.?":{}|<>]/, "Must include at least one special character"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
});

const CreatePassword = () => {
    const location = useLocation();
    const userId = location.state?.userId;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const formik = useFormik({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!userId) {
                toast.error("User ID not found. Please login again.");
                console.error("User ID not found. Please login.");
                return <navigate to="/create-account" />;
            }
            setLoading(true);
            try {
                const response = await axios.post(
                    `${apiConfig.apiBaseURL}/createPassword`,
                    {
                        id: userId,
                        password: values.password,
                        confirmPassword: values.confirmPassword,
                    },
                    {
                        headers: {
                            "x-api-key": apiConfig["x-api-key"],
                            "Content-Type": "application/json"
                        }
                    }
                );

                if (response.data.success) {
                    localStorage.setItem("userId", userId);
                    MySwal.fire({
                        allowOutsideClick: false,
                        imageUrl: IocnSuccess,
                        title: "Congratulations!",
                        text: location.state?.signUp
                            ? "You have successfully created your password and are just two steps away from completing your registration!"
                            : "You have successfully updated your Password",
                        confirmButtonText: location.state?.signUp
                            ? "Create Your Profile"
                            : "Back to Login",
                        customClass: {
                            container: "custom-swal-container",
                            popup: "custom-swal-popup",
                        },
                    }).then(() => {
                        //console.log(location.state?.signUp, "====checking");
                        if (!location.state?.signUp) {
                            navigate("/", { replace: true });
                        } else {
                            navigate("/create-profile", { replace: true });
                        }

                        //window.location.href = "/create-profile";
                    });
                }
            } catch (error) {
                console.error("Error creating password:", error.response?.data || error.message);
                MySwal.fire({
                    title: "Error",
                    text: error.response?.data?.message || "Something went wrong!",
                    icon: "error",
                    confirmButtonText: "Try Again",
                });
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <section className="credential-page">
            <Container fluid>
                <Row className="justify-content-md-center" style={{ minHeight: "100vh" }}>
                    <Col lg={6} className="p-0">
                        <div className="left-side d-md-flex align-items-center justify-content-center bg-light">
                            <QuoteSlider quotes={quotes} />
                        </div>
                    </Col>
                    <Col lg={6} className="right-side d-flex flex-column justify-content-center">
                        <div className="mb-4 text-center">
                            <figure className="mb-4">
                                <LazyLoadImage src={IconHeader} alt="Logo" className="img-fluid" />
                            </figure>
                            <h5 className="fw-bold text-dark">Create Password</h5>
                        </div>
                        <Form onSubmit={formik.handleSubmit} noValidate>
                            <Form.Group className="mb-3 position-relative">
                                <Form.Label>New Password*</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter new password"
                                        {...formik.getFieldProps("password")}
                                        isInvalid={formik.touched.password && !!formik.errors.password}
                                        className="p-3"
                                        style={{ backgroundImage: "none" }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.password}
                                    </Form.Control.Feedback>
                                    <span
                                        className="position-absolute cursor-pointer"
                                        style={{ zIndex: 9, right: '30px', top: '17px' }}
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className="mb-3 position-relative">
                                <Form.Label>Confirm Password*</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showConfirmPassword ? "text" : "password"}
                                        placeholder="Confirm password"
                                        {...formik.getFieldProps("confirmPassword")}
                                        isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                                        className="p-3"
                                        style={{ backgroundImage: "none" }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.confirmPassword}
                                    </Form.Control.Feedback>
                                    <span
                                        className="position-absolute cursor-pointer"
                                        style={{ zIndex: 9, right: '30px', top: '17px' }}
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </InputGroup>
                            </Form.Group>
                            <Button
                                variant="primary"
                                className="mt-4 border-0 text-white font-16 px-5 py-3 w-100"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? "Submit..." : "Submit"}
                            </Button>
                        </Form>
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export default CreatePassword;